import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, Field, Input, Modal, Select, Spinner, useToast } from "@aigntx/ui";
import { endpoints } from "../api";
import { RaiseFormModal, statusTone } from "./ProjectFormsPage";
import type {
  Contract,
  FormType,
  FormsMeta,
  MoneyLine,
  Phase,
  ProjectDetail,
  ProjectForm,
  ProjectSummary,
  SitePhoto,
} from "../types";

type Tab = "money" | "programme" | "correspondence" | "photos";

const TABS: Tab[] = ["money", "programme", "correspondence", "photos"];

/**
 * What this desk is about: money, time, safety and progress. Submittals and
 * inspections are register work and are not offered here.
 */
const QUICK_FAMILIES = ["variations", "payments", "hse", "reports", "correspondence"];

/** Preference order when a party can raise more of them than fit on the strip. */
const QUICK_ORDER = ["RFC", "VO", "PAY", "RFI", "SI", "SPR", "VE", "NPV", "DVO"];

const QUICK_LIMIT = 6;

/** Which families count as correspondence you read rather than paperwork you file. */
const LETTER_FAMILIES = ["correspondence", "reports", "quality", "hse"];

/**
 * The supervision desk for one project: what the contract says, what the
 * variations and payments have done to it, whether the programme is holding,
 * and what the site looks like.
 *
 * Everything here is read from forms that completed their approval route — no
 * number is typed in twice, so this page cannot disagree with the register.
 */
export function SupervisionPage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((x) => x === params.get("tab")) ?? "money") as Tab;

  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [summary, setSummary] = useState<ProjectSummary | null>(null);
  const [forms, setForms] = useState<ProjectForm[]>([]);
  const [catalog, setCatalog] = useState<FormType[]>([]);
  const [meta, setMeta] = useState<FormsMeta | null>(null);
  const [photos, setPhotos] = useState<SitePhoto[] | null>(null);
  const [raising, setRaising] = useState<FormType | null>(null);
  const [editContract, setEditContract] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      endpoints.getProject(id),
      endpoints.projectSummary(id),
      endpoints.listForms(id),
      endpoints.formsCatalog(),
      endpoints.formsMeta(id),
    ])
      .then(([p, s, f, c, m]) => {
        setProject(p);
        setSummary(s);
        setForms(f);
        setCatalog(c);
        setMeta(m);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [id]);

  // The gallery is the heaviest thing here, so it only loads if it is asked for.
  useEffect(() => {
    if (tab !== "photos" || photos !== null) return;
    endpoints
      .listPhotos(id)
      .then(setPhotos)
      .catch(() => setPhotos([]));
  }, [tab, photos, id]);

  const money = project?.myAbilities.includes("approve") ?? false;
  const canEdit = project?.myAbilities.includes("edit") ?? false;
  // Baselining is the same bar as the contract sum: approve, not edit. Anyone
  // who can type a date must not be able to move the goalposts they are judged
  // against.
  const canBaseline = project?.myAbilities.includes("approve") ?? false;

  // Built from what this party may actually originate, not from a fixed list.
  // A fixed list led with RFI and PAY — both Contractor-raised — so the
  // consultant, the only person who uses this desk, silently lost two of the
  // six every time. Derived, the strip is right for whoever is looking at it.
  const quick = useMemo(
    () =>
      catalog
        .filter((c) => QUICK_FAMILIES.includes(c.family) && meta?.canRaise.includes(c.code))
        .sort((a, b) => {
          const rank = (code: string) => {
            const i = QUICK_ORDER.indexOf(code);
            return i === -1 ? QUICK_ORDER.length : i;
          };
          return rank(a.code) - rank(b.code);
        })
        .slice(0, QUICK_LIMIT),
    [catalog, meta],
  );

  const letters = useMemo(() => {
    const family = new Map(catalog.map((c) => [c.code, c.family]));
    return forms.filter((f) => LETTER_FAMILIES.includes(family.get(f.code) ?? ""));
  }, [forms, catalog]);

  async function refreshSummary() {
    setSummary(await endpoints.projectSummary(id));
  }

  const [baselining, setBaselining] = useState(false);

  async function saveBaseline(reason: string) {
    const phases = await endpoints.setBaseline(id, reason);
    setProject((p) => (p ? { ...p, phases } : p));
    await refreshSummary();
    setBaselining(false);
    toast.success(t("sup.baselineSaved"));
  }

  function onPhaseSaved(updated: Phase) {
    setProject((p) =>
      p ? { ...p, phases: p.phases.map((x) => (x.id === updated.id ? updated : x)) } : p,
    );
    void refreshSummary();
  }

  if (error)
    return (
      <div className="page">
        <Card>{error}</Card>
      </div>
    );
  if (!project || !summary)
    return (
      <div className="page center">
        <Spinner />
      </div>
    );

  const c = summary.contract;

  return (
    <div className="page">
      <div className="crumb">
        <Link to="/projects">{t("projects.title")}</Link> <span className="muted">/ </span>
        <Link to={`/projects/${id}`}>{project.name}</Link>{" "}
        <span className="muted">/ {t("sup.title")}</span>
      </div>

      <header className="page__head between">
        <div>
          <h1 className="page__title">{t("sup.title")}</h1>
          <p className="page__sub">{t("sup.hint")}</p>
        </div>
        <div className="head-actions">
          {quick.length > 0 && (
            <Select
              value=""
              onChange={(e) => {
                const spec = quick.find((q) => q.code === e.target.value);
                if (spec) setRaising(spec);
              }}
            >
              <option value="">{t("sup.quickRaise")}</option>
              {quick.map((q) => (
                <option key={q.code} value={q.code}>
                  {q.code} — {q.title}
                </option>
              ))}
            </Select>
          )}
          <Link className="ax-btn ax-btn--outline ax-btn--sm" to={`/projects/${id}/forms`}>
            {t("forms.open")}
          </Link>
        </div>
      </header>

      {/* the four numbers anyone walking past the screen should be able to read */}
      <div className="sup-strip">
        <Stat
          label={t("sup.adjusted")}
          value={mny(summary.adjustedContractSum ?? c.contractSum)}
          note={
            summary.variationsApproved
              ? t("sup.plusVariations", { amount: mny(summary.variationsApproved) })
              : t("sup.noVariations")
          }
        />
        <Stat
          label={t("sup.certified")}
          value={mny(summary.certifiedTotal)}
          note={t("sup.ofClaimed", { amount: mny(summary.claimedTotal) })}
          tone={summary.certifiedTotal < summary.claimedTotal ? "warn" : "good"}
        />
        <Stat
          label={t("sup.timeImpact")}
          value={summary.eotDaysApproved ? `+${summary.eotDaysApproved} ${t("sup.days")}` : "—"}
          note={
            summary.phasesLate
              ? t("sup.phasesLate", { count: summary.phasesLate, days: summary.worstSlipDays })
              : t("sup.onProgramme")
          }
          tone={summary.phasesLate ? "danger" : "good"}
        />
        <Stat
          label={t("sup.safety")}
          value={String(summary.safetyOpen)}
          note={
            summary.safetyStopWork
              ? t("sup.stopWork", { count: summary.safetyStopWork })
              : t("sup.noStopWork")
          }
          tone={summary.safetyStopWork ? "danger" : summary.safetyOpen ? "warn" : "good"}
        />
      </div>

      <div className="segmented sup-tabs">
        {TABS.map((x) => (
          <button
            key={x}
            type="button"
            className={tab === x ? "seg is-on" : "seg"}
            onClick={() => setParams(x === "money" ? {} : { tab: x })}
          >
            {t(`sup.tab.${x}`)}
          </button>
        ))}
      </div>

      {tab === "money" && (
        <div className="detail-grid">
          <Card title={t("sup.contract")}>
            <div className="tab-toolbar">
              <p className="page__sub">{t("sup.contractHint")}</p>
              {money && (
                <Button size="sm" onClick={() => setEditContract(true)}>
                  {t("common.edit")}
                </Button>
              )}
            </div>
            <Row k={t("sup.contractNo")} v={c.contractNo || "—"} />
            <Row k={t("sup.contractSum")} v={mny(c.contractSum)} />
            <Row k={t("sup.contractStart")} v={c.contractStart || "—"} />
            <Row k={t("sup.contractFinish")} v={c.contractFinish || "—"} />
            <Row k={t("sup.retention")} v={pct(c.retentionPct)} />
            <Row k={t("sup.advance")} v={pct(c.advancePct)} />
            <Row
              k={t("sup.defects")}
              v={c.defectsMonths == null ? "—" : t("sup.months", { count: c.defectsMonths })}
            />
            {c.contractNotes && <p className="proj-desc">{c.contractNotes}</p>}
          </Card>

          <Card title={t("sup.impact")}>
            <p className="page__sub">{t("sup.impactHint")}</p>
            <Row k={t("sup.original")} v={mny(c.contractSum)} />
            <Row k={t("sup.approvedVariations")} v={mny(summary.variationsApproved)} />
            <Row k={t("sup.pendingVariations")} v={mny(summary.variationsPending)} />
            <Row k={t("sup.savings")} v={mny(summary.savingsAccepted)} />
            <div className="kv kv--total">
              <span className="kv__k">{t("sup.adjusted")}</span>
              <span className="kv__v">{mny(summary.adjustedContractSum ?? c.contractSum)}</span>
            </div>
            <Row
              k={t("sup.eot")}
              v={
                summary.eotDaysApproved || summary.eotDaysPending
                  ? `${summary.eotDaysApproved} ${t("sup.days")} (+${summary.eotDaysPending} ${t("sup.pending")})`
                  : "—"
              }
            />
            <Row
              k={t("sup.paid")}
              v={`${mny(summary.paidTotal)} / ${mny(summary.certifiedTotal)}`}
            />
            {/* The time side of the same sum. The money row above has always
                been here; this is the date it implies, which was being worked
                out by hand every month. */}
            <Row k={t("sup.contractFinish")} v={c.contractFinish || "—"} />
            <div className="kv kv--total">
              <span className="kv__k">{t("sup.adjustedFinish")}</span>
              <span className="kv__v">
                {summary.adjustedContractFinish || "—"}
                {summary.daysAgainstAdjustedFinish != null && (
                  <Badge tone={summary.daysAgainstAdjustedFinish > 0 ? "danger" : "good"}>
                    {summary.daysAgainstAdjustedFinish > 0
                      ? t("sup.overrunBy", { count: summary.daysAgainstAdjustedFinish })
                      : t("sup.daysInHand", { count: -summary.daysAgainstAdjustedFinish })}
                  </Badge>
                )}
              </span>
            </div>
          </Card>

          <Lines
            title={t("sup.variations")}
            hint={t("sup.variationsHint")}
            lines={summary.variationLines}
            projectId={id}
            empty={t("sup.noneYet")}
            showDays
          />
          <Lines
            title={t("sup.payments")}
            hint={t("sup.paymentsHint")}
            lines={summary.paymentLines}
            projectId={id}
            empty={t("sup.noneYet")}
          />
          <Lines
            title={t("sup.ve")}
            hint={t("sup.veHint")}
            lines={summary.veLines}
            projectId={id}
            empty={t("sup.noneYet")}
          />
        </div>
      )}

      {tab === "programme" && (
        <Card title={t("sup.tab.programme")}>
          <p className="page__sub">{t("sup.programmeHint")}</p>
          <div className="sup-baseline">
            <div>
              {summary.baseline.setOn ? (
                <>
                  <strong>{t("sup.baselinedOn", { date: summary.baseline.setOn })}</strong>
                  {summary.baseline.setBy && ` — ${summary.baseline.setBy}`}
                  {summary.baseline.revisions > 0 && (
                    <Badge tone="accent">
                      {t("sup.replanned", { count: summary.baseline.revisions })}
                    </Badge>
                  )}
                  {summary.baseline.note && (
                    <div className="muted">{summary.baseline.note}</div>
                  )}
                </>
              ) : (
                <span className="muted">{t("sup.noBaseline")}</span>
              )}
            </div>
            {canBaseline && (
              <Button size="sm" variant="outline" onClick={() => setBaselining(true)}>
                {summary.baseline.setOn ? t("sup.rebaseline") : t("sup.setBaseline")}
              </Button>
            )}
          </div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t("sup.phase")}</th>
                  <th>{t("sup.baseline")}</th>
                  <th>{t("sup.planned")}</th>
                  <th>{t("sup.actual")}</th>
                  <th>{t("sup.progress")}</th>
                  <th>{t("sup.slip")}</th>
                  <th>{t("sup.slipVsBaseline")}</th>
                  {canEdit && <th aria-label="actions" />}
                </tr>
              </thead>
              <tbody>
                {project.phases.map((ph) => (
                  <PhaseRow
                    key={ph.id}
                    projectId={id}
                    phase={ph}
                    canEdit={canEdit}
                    onSaved={onPhaseSaved}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === "correspondence" && (
        <Card title={t("sup.tab.correspondence")}>
          <p className="page__sub">{t("sup.correspondenceHint")}</p>
          {letters.length === 0 ? (
            <p className="muted">{t("sup.noneYet")}</p>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>{t("forms.number")}</th>
                    <th>{t("forms.subject")}</th>
                    <th>{t("forms.raisedBy")}</th>
                    <th>{t("forms.state")}</th>
                    <th>{t("sup.when")}</th>
                  </tr>
                </thead>
                <tbody>
                  {letters.map((f) => (
                    <tr key={f.id} className={f.canAct ? "is-mine" : undefined}>
                      <td>
                        <Link className="mono form-no" to={`/projects/${id}/forms/${f.id}`}>
                          {f.number}
                        </Link>
                        <div className="u-mail">{f.title}</div>
                      </td>
                      <td>{f.subject}</td>
                      <td>
                        <div className="u-name">{f.originatorName}</div>
                        <div className="u-mail">{f.originatorGroupName || f.originatorParty}</div>
                      </td>
                      <td>
                        <Badge tone={statusTone(f)}>
                          {f.status === "closed" ? f.outcomeLabel || t("forms.closed") : f.stepLabel}
                        </Badge>
                      </td>
                      <td className="mono">
                        {f.createdAt ? new Date(f.createdAt).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === "photos" && (
        <Card title={t("sup.tab.photos")}>
          <p className="page__sub">{t("sup.photosHint")}</p>
          {photos === null ? (
            <div className="center">
              <Spinner />
            </div>
          ) : photos.length === 0 ? (
            <p className="muted">{t("sup.noPhotos")}</p>
          ) : (
            <div className="photo-grid">
              {photos.map((p) => (
                <Link
                  key={p.id}
                  className="photo"
                  to={`/projects/${id}/forms/${p.formId}`}
                  title={p.filename}
                >
                  <img
                    src={endpoints.photoUrl(id, p.formId, p.id)}
                    alt={p.subject || p.filename}
                    loading="lazy"
                  />
                  <div className="photo__cap">
                    <span className="mono">{p.formNumber}</span>
                    <span className="muted">{new Date(p.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div className="photo__by">{p.uploaderName}</div>
                </Link>
              ))}
            </div>
          )}
        </Card>
      )}

      {baselining && (
        <BaselineModal
          existing={Boolean(summary.baseline.setOn)}
          onClose={() => setBaselining(false)}
          onSave={saveBaseline}
        />
      )}

      {editContract && (
        <ContractModal
          projectId={id}
          contract={c}
          onClose={() => setEditContract(false)}
          onSaved={(saved) => {
            setEditContract(false);
            setSummary((s) => (s ? { ...s, contract: saved } : s));
            setProject((p) => (p ? { ...p, contract: saved } : p));
            void refreshSummary();
            toast.success(t("sup.contractSaved"));
          }}
        />
      )}
      {raising && (
        <RaiseFormModal
          projectId={id}
          spec={raising}
          onClose={() => setRaising(null)}
          onRaised={(f) => {
            setRaising(null);
            setForms((list) => [f, ...list]);
            void refreshSummary();
            toast.success(t("forms.raised", { number: f.number }));
          }}
        />
      )}
    </div>
  );
}

/* ---- pieces ---- */

function Stat({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note: string;
  tone?: "good" | "warn" | "danger";
}) {
  return (
    <div className={`sup-stat${tone ? ` sup-stat--${tone}` : ""}`}>
      <div className="sup-stat__label">{label}</div>
      <div className="sup-stat__value">{value}</div>
      <div className="sup-stat__note">{note}</div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="kv">
      <span className="kv__k">{k}</span>
      <span className="kv__v">{v}</span>
    </div>
  );
}

function Lines({
  title,
  hint,
  lines,
  projectId,
  empty,
  showDays = false,
}: {
  title: string;
  hint: string;
  lines: MoneyLine[];
  projectId: string;
  empty: string;
  showDays?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Card title={title} className="grid__wide">
      <p className="page__sub">{hint}</p>
      {lines.length === 0 ? (
        <p className="muted">{empty}</p>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>{t("forms.number")}</th>
                <th>{t("forms.subject")}</th>
                <th>{t("sup.amount")}</th>
                {showDays && <th>{t("sup.days")}</th>}
                <th>{t("forms.state")}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((ln) => (
                <tr key={ln.id}>
                  <td>
                    <Link className="mono form-no" to={`/projects/${projectId}/forms/${ln.id}`}>
                      {ln.number}
                    </Link>
                  </td>
                  <td>{ln.subject || "—"}</td>
                  <td className="mono num">{mny(ln.amount)}</td>
                  {showDays && <td className="mono num">{ln.days || "—"}</td>}
                  <td>
                    <Badge tone={ln.status === "closed" ? "good" : "accent"}>
                      {ln.status === "closed" ? t("forms.closed") : t("sup.inProgress")}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function PhaseRow({
  projectId,
  phase,
  canEdit,
  onSaved,
}: {
  projectId: string;
  phase: Phase;
  canEdit: boolean;
  onSaved: (p: Phase) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({
    plannedStart: phase.plannedStart,
    plannedFinish: phase.plannedFinish,
    actualStart: phase.actualStart,
    actualFinish: phase.actualFinish,
    progressPct: phase.progressPct ?? 0,
  });

  async function save() {
    setBusy(true);
    try {
      onSaved(await endpoints.updatePhase(projectId, phase.id, draft));
      setEditing(false);
    } finally {
      setBusy(false);
    }
  }

  if (editing)
    return (
      <tr>
        <td>{phase.name}</td>
        <td className="mono muted">
          {/* the agreed dates are not editable here — they change only by
              re-baselining, which has to be a deliberate, recorded act */}
          {phase.baselineFinish || phase.baselineStart
            ? `${phase.baselineStart || "—"} → ${phase.baselineFinish || "—"}`
            : t("sup.notBaselined")}
        </td>
        <td className="phase-dates">
          <Input type="date" value={draft.plannedStart} onChange={(e) => setDraft((d) => ({ ...d, plannedStart: e.target.value }))} />
          <Input type="date" value={draft.plannedFinish} onChange={(e) => setDraft((d) => ({ ...d, plannedFinish: e.target.value }))} />
        </td>
        <td className="phase-dates">
          <Input type="date" value={draft.actualStart} onChange={(e) => setDraft((d) => ({ ...d, actualStart: e.target.value }))} />
          <Input type="date" value={draft.actualFinish} onChange={(e) => setDraft((d) => ({ ...d, actualFinish: e.target.value }))} />
        </td>
        <td>
          <Input
            type="number"
            min={0}
            max={100}
            value={String(draft.progressPct)}
            onChange={(e) => setDraft((d) => ({ ...d, progressPct: Number(e.target.value) }))}
          />
        </td>
        <td className="muted">—</td>
        <td className="muted">—</td>
        <td className="right">
          <Button size="sm" loading={busy} onClick={() => void save()}>
            {t("common.save")}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
            {t("common.cancel")}
          </Button>
        </td>
      </tr>
    );

  const slip = phase.slipDays;
  const baseSlip = phase.baselineSlipDays;
  return (
    <tr>
      <td>
        <div className="u-name">{phase.name}</div>
        <Badge tone={phase.status === "done" ? "good" : phase.status === "active" ? "accent" : "neutral"}>
          {t(`phase.${phase.status}`)}
        </Badge>
      </td>
      <td className="mono">
        {phase.baselineFinish || phase.baselineStart ? (
          span(phase.baselineStart, phase.baselineFinish)
        ) : (
          <span className="muted">{t("sup.notBaselined")}</span>
        )}
      </td>
      <td className="mono">{span(phase.plannedStart, phase.plannedFinish)}</td>
      <td className="mono">{span(phase.actualStart, phase.actualFinish)}</td>
      <td>
        <div className="bar">
          <span style={{ width: `${phase.progressPct ?? 0}%` }} />
        </div>
        <span className="mono">{phase.progressPct ?? 0}%</span>
      </td>
      <td>
        {slip == null ? (
          <span className="muted">—</span>
        ) : slip > 0 ? (
          <Badge tone="danger">{t("sup.lateBy", { count: slip })}</Badge>
        ) : (
          <Badge tone="good">{t("sup.onTime")}</Badge>
        )}
      </td>
      <td>
        {baseSlip == null ? (
          <span className="muted">—</span>
        ) : baseSlip > 0 ? (
          <Badge tone="danger">{t("sup.lateBy", { count: baseSlip })}</Badge>
        ) : (
          <Badge tone="good">{t("sup.onTime")}</Badge>
        )}
      </td>
      {canEdit && (
        <td className="right">
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
            {t("common.edit")}
          </Button>
        </td>
      )}
    </tr>
  );
}

/**
 * Freeze the planned dates as the agreed programme.
 *
 * Re-baselining asks for a reason and says plainly that the previous one is
 * kept — so the person doing it understands they are recording a re-plan, not
 * quietly erasing what was agreed.
 */
function BaselineModal({
  existing,
  onClose,
  onSave,
}: {
  existing: boolean;
  onClose: () => void;
  onSave: (reason: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      await onSave(reason.trim());
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={existing ? t("sup.rebaseline") : t("sup.setBaseline")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button loading={busy} disabled={existing && !reason.trim()} onClick={() => void submit()}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <p className="page__sub">
        {existing ? t("sup.rebaselineHint") : t("sup.setBaselineHint")}
      </p>
      {existing && (
        <Field label={t("sup.rebaselineReason")}>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("sup.rebaselineReasonHint")}
          />
        </Field>
      )}
    </Modal>
  );
}

function ContractModal({
  projectId,
  contract,
  onClose,
  onSaved,
}: {
  projectId: string;
  contract: Contract;
  onClose: () => void;
  onSaved: (c: Contract) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Contract>(contract);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function set<K extends keyof Contract>(key: K, value: Contract[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }
  const numeric = (v: string) => (v === "" ? null : Number(v));

  async function submit() {
    setBusy(true);
    setError("");
    try {
      onSaved(await endpoints.saveContract(projectId, draft));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={t("sup.contract")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      {error && <div className="form-err">{error}</div>}
      <Field label={t("sup.contractNo")}>
        <Input value={draft.contractNo} onChange={(e) => set("contractNo", e.target.value)} />
      </Field>
      <Field label={t("sup.contractSum")} hint={t("sup.contractSumHint")}>
        <Input
          type="number"
          value={draft.contractSum == null ? "" : String(draft.contractSum)}
          onChange={(e) => set("contractSum", numeric(e.target.value))}
        />
      </Field>
      <Field label={t("sup.contractStart")}>
        <Input type="date" value={draft.contractStart} onChange={(e) => set("contractStart", e.target.value)} />
      </Field>
      <Field label={t("sup.contractFinish")}>
        <Input type="date" value={draft.contractFinish} onChange={(e) => set("contractFinish", e.target.value)} />
      </Field>
      <Field label={t("sup.retention")}>
        <Input
          type="number"
          value={draft.retentionPct == null ? "" : String(draft.retentionPct)}
          onChange={(e) => set("retentionPct", numeric(e.target.value))}
        />
      </Field>
      <Field label={t("sup.advance")}>
        <Input
          type="number"
          value={draft.advancePct == null ? "" : String(draft.advancePct)}
          onChange={(e) => set("advancePct", numeric(e.target.value))}
        />
      </Field>
      <Field label={t("sup.defects")}>
        <Input
          type="number"
          value={draft.defectsMonths == null ? "" : String(draft.defectsMonths)}
          onChange={(e) => set("defectsMonths", numeric(e.target.value))}
        />
      </Field>
      <Field label={t("sup.contractNotes")}>
        <Input value={draft.contractNotes} onChange={(e) => set("contractNotes", e.target.value)} />
      </Field>
    </Modal>
  );
}

/* ---- formatting ---- */

function mny(v: number | null | undefined): string {
  if (v == null) return "—";
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}
function pct(v: number | null): string {
  return v == null ? "—" : `${v}%`;
}
function span(from: string, to: string): string {
  if (!from && !to) return "—";
  return `${from || "…"} → ${to || "…"}`;
}
